import { createFileRoute, useRouter } from '@tanstack/react-router'
import { EmptyState, Link, List } from '~/elements'
import { useUsage } from '~/hooks'
import { Separator, TooltipProvider, Button, Skeleton, showDialog } from '@bpinternal/ui-kit-next'
import { Boundary, PageActionPortal, UsageProgressBar } from '~/components'
import { Changelog } from '~/assets'
import { showUpsellDialog, useSuspenseQuery } from '~/services'
import { Role, RoleNames } from '~/features/users/types'
import { useEffect, useCallback } from 'react'
import { SidebarLayout } from '~/layouts'
import { QUOTA_TYPE_USER_FACING_PROPERTIES_MAP } from '~/features/usage/constants'
import { z } from 'zod'
import { Plus } from 'lucide-react'
import { MemberRow } from '~/features/members/components/MemberRow'
import { AddMemberDialog } from '~/features/members/components'

const validateSearch = z.object({
  email: z.string().optional(),
  level: z.enum(Object.keys(RoleNames) as [Role, ...Role[]]).optional(),
})

export const Route = createFileRoute('/workspaces/$workspaceId/settings/members')({
  component: Component,
  validateSearch,
})

function Component() {
  const router = useRouter()
  const workspaceId = Route.useParams().workspaceId
  const { email: initialEmail, level: initialRole } = Route.useSearch()
  const { workspace_member_count } = useUsage({
    workspaceId,
    quotas: ['workspace_member_count'],
  })

  const showAddMemberDialog = useCallback(() => {
    return showDialog(
      (props) => (
        <AddMemberDialog {...props} workspaceId={workspaceId} initEmail={initialEmail} initRole={initialRole} />
      ),
      {
        title: 'Add new member',
        description: 'An invitation will be sent to the email address you provide.',
      }
    )
  }, [workspaceId, initialEmail, initialRole])

  useEffect(() => {
    if (initialEmail) {
      showAddMemberDialog()
    }
  }, [initialEmail, showAddMemberDialog])

  return (
    <TooltipProvider>
      <Boundary
        className="h-96"
        suspenseFallback={
          <div className="flex flex-col gap-4">
            <Skeleton className="h-8 w-24 self-end" />
            <List items={Array(5).fill(null)}>{<AuditRowSkeleton />}</List>
          </div>
        }
      >
        <PageActionPortal>
          <Button
            onClick={() =>
              workspace_member_count.value < workspace_member_count.quota
                ? showAddMemberDialog()
                : void showUpsellDialog({
                    quota: 'workspace_member_count',
                    workspaceId,
                  }).then(
                    () =>
                      void router.navigate({
                        to: '/workspaces/$workspaceId/billing',
                        params: { workspaceId },
                      })
                  )
            }
          >
            <Plus />
            Invite member
          </Button>
        </PageActionPortal>
        <SidebarLayout
          main={<Members />}
          rightSidebar={
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <div className="text-sm">
                  <UsageProgressBar
                    title={<span className="font-medium">Your Plan Limit</span>}
                    description={
                      <span className="text-xs text-gray-11">
                        {QUOTA_TYPE_USER_FACING_PROPERTIES_MAP['workspace_member_count'].description}
                      </span>
                    }
                    {...workspace_member_count}
                  />
                </div>

                <Link to={'/workspaces/$workspaceId/billing'} params={{ workspaceId }} className="text-blue-11">
                  {workspace_member_count.value >= workspace_member_count.quota ? (
                    <Button size={'sm'} variant="outline" color="red">
                      Get more seats
                    </Button>
                  ) : (
                    'Get more seats'
                  )}
                </Link>
              </div>
              <Separator className="h-px w-full" />
              <div className="flex flex-col gap-3">
                <span className="text-sm font-medium">Need help? Invite an Expert</span>
                <span className="text-xs text-gray-11">
                  Team up with a Botpress expert and get help to design, build and optimize your chatbot or integration.
                </span>
                <a
                  href="https://www.botpress.com/contact-us?ref=members-page"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size={'sm'} variant="outline" color="grass">
                    Find your expert
                  </Button>
                </a>
              </div>
            </div>
          }
        />
      </Boundary>
    </TooltipProvider>
  )
}

const Members = () => {
  const workspaceId = Route.useParams().workspaceId
  const workspaceMembers = useSuspenseQuery('workspaces_/$workspaceId_/members', { workspaceId }).data
  const { user } = Route.useRouteContext()

  return workspaceMembers.length ? (
    <div className="flex flex-col gap-4">
      <List items={workspaceMembers}>
        {(member) => <MemberRow currentUser={user} member={member} workspaceId={workspaceId} />}
      </List>
    </div>
  ) : (
    <EmptyState
      className="p-8"
      iconSize={6}
      icon={Changelog}
      title="You don't have any audits"
      description="You have not performed any actions that are audited. Keep using Botpress to see your actions here."
      primaryAction={
        <Link to="/workspaces/$workspaceId" params={{ workspaceId }}>
          <Button variant="outline">Back to workspace</Button>
        </Link>
      }
    />
  )
}

const AuditRowSkeleton = () => {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4 p-3">
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="ml-auto h-4 w-24" />
      </div>
    </div>
  )
}
